import React, { useState, useEffect, memo, useCallback } from 'react';
import * as Styled from './styled';
import { LinkButton, PageHeader } from '../../styles/components';
import {
  CenteredContainer,
  LoadingSkeleton,
  Icon,
  ReportNotifications,
  Dialog,
} from '../../components';
import ReportSummary from './report-summary/ReportSummary';
import {
  fetchReportSnapshot,
  fetchReportExport,
  fetchReportArchives,
  fetchReportExportCSV,
  fetchSingleStandardReportWithCache,
  postExportInforAp,
  postExportReportSFTP,
} from '../../api';
import { useRouter } from 'next/router';
import ReportLeaseTable from './report-leases-table/ReportLeaseTable';
import ReportResultsTitleBar from './report-results-title-bar/ReportResultsTitleBar';
import { StyledText, Button } from '@leasequery/lq.react-component-library';
import { ArchivedReport, ReportSnapshot, ReportType } from '../../models';
import { downloadFile, FileTypes } from '../../util';
import ArchiveDialog from '../report-results/archive-dialog/ArchiveDialog';
import withClient from '../../components/with-client/withClient';
import { noop, isCustomAPReport } from '../../util';
import {
  dialogBorderColor,
  dialogCoverType,
} from '../../components/dialog/Dialog';
import { InforErrorDetail } from '../../components/notifications/types';
import { InforErrorResponse } from './../../api/report/postExportInforAp';
import { useIsFrs102Enabled } from '../../util/hooks/useIsFrs102Enabled';
import { replaceIfrsLabels } from '../../util/frs102Helper';
// import { report } from 'process';
// import { truncate } from 'fs';

const renderInforErrorDetailComponent = (
  errorResponse: InforErrorResponse,
): JSX.Element => {
  if (
    !errorResponse ||
    !errorResponse.errors ||
    errorResponse.errorType !== 'Integration'
  ) {
    return null;
  }
  return <InforErrorDetail errors={errorResponse.errors} />;
};

function ReportResultsDetail(): JSX.Element {
  const router = useRouter();
  const { id } = router.query;
  const [isSinglePageView, setSinglePageView] = useState<boolean>(false);
  const [reportSnapshot, setReportSnapshot] = useState<ReportSnapshot>(null);

  const [archiveDialogIsOpen, setArchiveDialogIsOpen] = useState(false);

  const [archivedReportNames, setArchivedReportNames] = useState<string[]>();
  const [isV11Report, setIsV11Report] = useState<boolean>(false);
  const [isInforReport, setIsInforReport] = useState<boolean>(false);
  const [isAPReport, setIsAPReport] = useState<boolean>(false);
  const [isShowNotification, setIsShowNotification] = useState<boolean>(false);
  const [isShowSendingNotification, setIsShowSendingNotification] = useState<
    boolean
  >(false);
  const [isShowInforError, setIsShowInforError] = useState(false);
  const [inforErrorResponse, setInforErrorResponse] = useState(null);
  const [isShowSftpError, setIsShowSftpError] = useState(false);
  const [sftpErrorResponse, setSftpErrorResponse] = useState(null);
  
  const [isExportDisabled, setIsExportDisabled] = useState(false);

  const isFrs102Enabled = useIsFrs102Enabled();

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const snapshot = await fetchReportSnapshot(parseInt(id as string));
      if (snapshot.standardReportId) {
        const standardReport = await fetchSingleStandardReportWithCache(
          snapshot.clientId,
          snapshot.standardReportId,
        );
        setIsV11Report(standardReport.runType === 'v11');
        setIsInforReport(
          standardReport.runType === 'v10' || standardReport.runType === 'v11',
        );
        setIsAPReport(
            standardReport.runType === 'ap'
        );
      }
      setReportSnapshot(snapshot);
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const resp: ArchivedReport[] = await fetchReportArchives();
      setArchivedReportNames(resp.map((archivedReport) => archivedReport.name));
    };

    fetchData();
  }, []);

  const archiveHandler = useCallback(
    (taskId: number, archiveName: string) => {
      const updatedSnapshot = {
        ...reportSnapshot,
        taskId,
        isArchived: true,
        taskName: archiveName,
      };
      setReportSnapshot(updatedSnapshot);
    },
    [reportSnapshot],
  );

  const viewRecentResultsClickHandler = useCallback(() => {
    router.push('/results');
  }, [router]);

  const saveResultToArchiveHandler = useCallback(() => {
    setArchiveDialogIsOpen(true);
  }, []);

  const onExportExcelClicked = useCallback(
    (fileExtension: FileTypes): void => {
      const fileName =
        (reportSnapshot && reportSnapshot.reportName) || 'SavedReport';
      const ifrsFileName = replaceIfrsLabels(
        fileName,
        isFrs102Enabled
      );

      setIsExportDisabled(true);

      if (fileExtension === FileTypes.csv) {
        const fetchCSVExport = async (): Promise<void> => {
          const response = await fetchReportExportCSV(parseInt(id as string));
          downloadFile(response.body, fileName, fileExtension);
          setIsExportDisabled(false);
        };
        fetchCSVExport();
      } else {
        const fetchExportFile = async (): Promise<void> => {
          const response = await fetchReportExport(parseInt(id as string));
          downloadFile(response.body, ifrsFileName, fileExtension);
          setIsExportDisabled(false);
        };
        fetchExportFile();
      }

    },
    [id, isFrs102Enabled, reportSnapshot],
  );

  const getBorderColorFromError = useCallback((): string => {
    if (
      inforErrorResponse?.totalItemCount > inforErrorResponse?.errorItemCount
    ) {
      return dialogBorderColor.error;
    } else {
      return dialogBorderColor.fail;
    }
  }, [inforErrorResponse]);

  const handleNotificationClose = useCallback((): void => {
    setIsShowNotification(false);
  }, []);

  const handleSendingNotificationClose = useCallback((): void => {
    setIsShowSendingNotification(false);
  }, []);

  const handleInforErrorClose = useCallback((): void => {
    if (isInforReport) {
      setIsShowInforError(false);
    }

    if (isAPReport) {
      setIsShowSftpError(false);
    }
  }, [isInforReport, isAPReport]);

  const onSendToSFTPHandler = useCallback(
    async() => {
      const response = await postExportReportSFTP(
        reportSnapshot.clientId,
        reportSnapshot.id,
      );

      if (!response.success) {
        setSftpErrorResponse(response);
        setIsShowSftpError(true);
        setIsShowSendingNotification(false);
        return;
      }
      setIsShowSendingNotification(false);
      setIsShowNotification(true);
      const timer = setTimeout(() => {
        handleNotificationClose();
      }, 3000);
      return (): void => clearTimeout(timer);
    },
    [reportSnapshot, handleNotificationClose]
  );

  const onSendToInforHandler = useCallback(
    async (isSendSFTP: boolean) => {
      setIsShowSendingNotification(true);

      if (isSendSFTP) {
        const response = await postExportReportSFTP(
          reportSnapshot.clientId,
          reportSnapshot.id,
        );
        if (!response.success) {
          setInforErrorResponse(response);
          setIsShowInforError(true);
          setIsShowSendingNotification(false);
          return;
        }
      } else {
        const response = await postExportInforAp(
          reportSnapshot.clientId,
          reportSnapshot.id,
        );
        if (!response.success) {
          setInforErrorResponse(response);
          setIsShowInforError(true);
          setIsShowSendingNotification(false);
          return;
        }
      }
      setIsShowSendingNotification(false);
      setIsShowNotification(true);
      const timer = setTimeout(() => {
        handleNotificationClose();
      }, 3000);
      return (): void => clearTimeout(timer);
    },
    [reportSnapshot, handleNotificationClose],
  );

  useEffect(() => {
    // GAAP & IFRS Complete Disclosures Report
    if (reportSnapshot?.standardReportTypeDisplay === ReportType.disclosure) {
      setSinglePageView(true);
    }
    // GASB 87: Effect of Adoption - Statement of Activities
    // GASB 87: Effect of Adoption - Statement of Net Position
    else if (
      reportSnapshot?.standardReportTypeDisplay ===
      ReportType.effectOfStandardAdoptionPAndL
    ) {
      setSinglePageView(true);
    }
  }, [reportSnapshot]);

  return (
    <CenteredContainer>
      <div>
        <PageHeader>View and Save Report Results</PageHeader>
        <StyledText as="div">
          Your report results are below. Save the report criteria for future
          use. You can also edit the current report criteria and save over or
          save as a new report. Report Results are kept for 14 days.
        </StyledText>
      </div>
      <Styled.Section>
        <ReportSummary reportSnapshot={reportSnapshot} />
      </Styled.Section>
      {reportSnapshot && reportSnapshot.hasExtraColumn && (
          <Styled.WarningColumnsCap>
            <Icon name="warning" width={22} height={16} />
              Due to the size of this report, the data has been truncated to 100 columns. Please&nbsp;
              <LinkButton
                onClick={(): void => onExportExcelClicked(FileTypes.xlsx)}
                disabled={isExportDisabled}
                >
                  Export to Excel
              </LinkButton>
              &nbsp;to view all data.
          </Styled.WarningColumnsCap>
        )
      }
      {reportSnapshot && isCustomAPReport(reportSnapshot.reportName) && (
          <Styled.WarningColumnsCap>
            <Icon name="warning" width={22} height={16} />
              Due to the size of this report, data has been truncated. Please&nbsp;
              <LinkButton
                onClick={(): void => onExportExcelClicked(FileTypes.csv)}
                disabled={isExportDisabled}
                >
                  Export to CSV
              </LinkButton>
              &nbsp;to view all data.
          </Styled.WarningColumnsCap>
        )
      }
      <Styled.Section>
        <ReportResultsTitleBar
          reportSnapshot={reportSnapshot}
          onExportExcelClicked={onExportExcelClicked}
          isV11Report={isV11Report}
          isInforReport={isInforReport}
          isAPReport={isAPReport}
          onSendToInforFSMClicked={(): void => {
            onSendToInforHandler(false);
          }}
          onSendToInforSFTPClicked={(): void => {
            onSendToInforHandler(true);
          }}
          onSendToSFTPClicked={(): void => {
            onSendToSFTPHandler();
          }}
          isExportDisabled={isExportDisabled}
        />
        {reportSnapshot ? (
          <ReportLeaseTable
            isSinglePageView={isSinglePageView}
            reportSnapshot={reportSnapshot}
          />
        ) : (
          <LoadingSkeleton name="table" />
        )}
      </Styled.Section>
      {reportSnapshot && (
        <>
          <Styled.FooterWrapper>
            <Styled.FooterTextBar>
              <Icon name="clearRight" />
              &nbsp; Results are saved for 14 days. To permanently save report
              results, select Save Results to Archive
            </Styled.FooterTextBar>
            <Styled.ButtonsBar>
              {/* TODO: need to get the Save Results to Archive button to go away after saving to archive success  */}
              {!reportSnapshot.isArchived && (
                <Button
                  buttonType="primary"
                  onClick={saveResultToArchiveHandler}
                >
                  Save Results to Archive
                </Button>
              )}
              <Button
                buttonType="secondary"
                onClick={viewRecentResultsClickHandler}
              >
                View All Recent Results
              </Button>
            </Styled.ButtonsBar>
            {archiveDialogIsOpen && (
              <ArchiveDialog
                reportResultId={Number(id)}
                isOpen={archiveDialogIsOpen}
                onStateChange={setArchiveDialogIsOpen}
                onArchive={archiveHandler}
                archivedReportNames={archivedReportNames}
              />
            )}
          </Styled.FooterWrapper>
        </>
      )}
      <Dialog
        isOpen={isShowInforError}
        onStateChange={noop}
        coverType={dialogCoverType.clear}
        borderColor={getBorderColorFromError()}
        maxWidth="60rem"
        detailInformation={renderInforErrorDetailComponent(inforErrorResponse)}
      >
        <ReportNotifications
          notificationType="inforError"
          onClose={handleInforErrorClose}
          withoutClose={true}
          errorResponse={inforErrorResponse}
        />
      </Dialog>

      <Dialog
        isOpen={isShowNotification}
        onStateChange={noop}
        withoutCover={true}
      >
        <ReportNotifications
          notificationType="sentAPReport"
          onClose={handleNotificationClose}
        />
      </Dialog>

      <Dialog
        isOpen={isShowSftpError}
        onStateChange={noop}
        withoutCover={true}
        coverType={dialogCoverType.clear}
        borderColor={getBorderColorFromError()}
        maxWidth="60rem"
      >
        <ReportNotifications
          notificationType="sentSFTPError"
          onClose={handleInforErrorClose}
          withoutClose={true}
          errorResponse={sftpErrorResponse}
        />
      </Dialog>

      <Dialog
        isOpen={isShowNotification}
        onStateChange={noop}
        withoutCover={true}
      >
        <ReportNotifications
          notificationType="sentSFTPReport"
          onClose={handleNotificationClose}
        />
      </Dialog>

      <Dialog
        isOpen={isShowSendingNotification}
        onStateChange={noop}
      >
        <ReportNotifications
          notificationType="sendingAPReport"
          onClose={handleSendingNotificationClose}
          withoutClose={true}
        />
      </Dialog>
    </CenteredContainer>
  );
}

export default memo(withClient(ReportResultsDetail));
